/*
 * The Mako page's workspace (sections/mako-tools.liquid): a small working
 * Premiere Pro timeline with the Mako panel beside it.
 *
 * Clips can be selected (click; shift, cmd or a tap adds), the playhead
 * dragged on the ruler, the sequence played, and each Mako button does its
 * job on this timeline, the way the plugin does it in Premiere:
 *
 *   Adjustment Layer / Color Matte  over the selected clips, on the first
 *     free track above them (a track is added when none is free); nothing
 *     selected: five seconds from the playhead. Color Matte asks for a
 *     color first. Shift-click Adjustment Layer: one per clip; Ctrl/⌘-click:
 *     a short one on every cut.
 *   New Sequence       a new, empty sequence with the open one's settings.
 *   Staircase          the selected clips, in time order, a track higher each.
 *   Unstaircase        the selected clips back down to one track.
 *   Freeze Frame       the clip itself holds the playhead's frame, in place,
 *     with no new layer (0.01% speed); Shift-click: 3 s from the playhead.
 *   Fit to Frame       the clip scaled to the frame (Shift-click fills it).
 *   Clear Effects      the selected clips' effects go; Motion stays.
 *   QC Check           closes empty gaps, marks tiny clips (Shift-click
 *     clears the markers).
 *   Transparency Grid  the checkerboard behind the frame, on and off.
 *   Nest / Unnest      the selected clips into a nest in their place, and a
 *     nest's clips back in place.
 *   Delete Empty Tracks  every empty track goes.
 *
 * These follow the plugin (Mako 1.2.2, README and host.jsx). Clips that
 * change track or time slide there from where they were. Undo and Reset
 * sit in the timeline's header. When the workspace first comes into view it
 * presses every button by itself, then resets and hands over; any touch
 * stops that. With reduced motion nothing plays or tours.
 */
(() => {
  const SPAN = 12; // seconds across the timeline
  const FPS = 25;
  const MAX_TRACKS = 7;
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');

  const pad = (n) => String(n).padStart(2, '0');
  const timecode = (t) => {
    const s = Math.floor(t);
    return `00:00:${pad(s)}:${pad(Math.min(FPS - 1, Math.floor((t - s) * FPS)))}`;
  };
  const pct = (t) => ((t / SPAN) * 100).toFixed(3);
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const clone = (o) => JSON.parse(JSON.stringify(o));
  const seconds = (t) => `${(Math.round(t * 10) / 10).toFixed(1)} s`;

  const NEST = 'Nested Sequence 01';
  const BROLL = 'Longboard [120%]';
  const TINY = 2 / FPS; // QC Check: a clip this short is a leftover of a cut
  const CUT = 0.4; // Ctrl-click Adjustment Layer: the layer on each cut
  const HOLD = 3; // Shift-click Freeze Frame: seconds from the playhead
  // A warm grade on the interview, for Clear Effects to take off.
  const LOOK = 'sepia(0.35) saturate(1.6) hue-rotate(-14deg) contrast(1.08)';
  const QC = { gap: '#ff453a', tiny: '#bf5af2' };

  const start = () => ({
    current: 0,
    nextId: 10,
    sequences: [
      {
        name: 'Sequence 01',
        v: 3,
        a: 2,
        markers: [],
        clips: [
          { id: 1, kind: 'video', track: 1, start: 0.3, end: 3.6, type: 'clip', label: 'Interview', media: 'interview', link: 2, fx: true, look: true },
          { id: 2, kind: 'audio', track: 1, start: 0.3, end: 3.6, type: 'clip', label: 'Interview', link: 1 },
          {
            id: 3, kind: 'video', track: 1, start: 4.0, end: 8.2, type: 'nest', label: NEST, link: 4,
            // what's inside the nest, relative to its start
            pieces: [
              { kind: 'video', offset: 0, start: 0, end: 4.2, type: 'clip', label: BROLL, media: 'broll', scale: 0.72, speed: 1.2, fx: true, pair: 'b' },
              // a picture-in-picture of the interview, upper right
              { kind: 'video', offset: 1, start: 0.8, end: 3.6, type: 'clip', label: 'Interview PiP', media: 'interview', scale: 0.34, pos: [30, -28], from: 3.3, fx: true },
              { kind: 'audio', offset: 0, start: 0, end: 4.2, type: 'clip', label: BROLL, pair: 'b' },
            ],
          },
          { id: 4, kind: 'audio', track: 1, start: 4.0, end: 8.2, type: 'nest', label: NEST, link: 3 },
          // smaller than the frame, for Fit to Frame
          { id: 5, kind: 'video', track: 1, start: 8.2, end: 11.6, type: 'clip', label: 'Sunset', media: 'outro', scale: 0.8 },
          // two frames left over from a cut, for QC Check to find
          { id: 6, kind: 'video', track: 1, start: 11.6, end: 11.6 + TINY, type: 'clip', label: 'Sunset', media: 'outro', scale: 0.8, from: 3.4 },
        ],
      },
    ],
  });

  const init = (root) => {
    if (root.dataset.makoReady) return;
    root.dataset.makoReady = 'true';

    const q = (sel) => root.querySelector(sel);
    // Modifier clicks are written the Mac way; Windows names them Shift and Ctrl.
    const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent;
    if (!/mac|iphone|ipad|ipod/i.test(platform)) {
      root.querySelectorAll('[data-mk-os]').forEach((el) => {
        el.textContent = el.textContent.replace(/⌘- or ⌃-|⌘-|⌃-/g, 'Ctrl-').replace(/⇧-/g, 'Shift-').replace(/⌥-/g, 'Alt-');
      });
    }
    const stage = q('[data-mako-stage]');
    const screen = q('[data-mako-screen]');
    const tracksEl = q('[data-mako-tracks]');
    const body = q('[data-mako-tlbody]');
    const ruler = q('[data-mako-ruler]');
    const tabsEl = q('[data-mako-tabs]');
    const statusEl = q('[data-mako-status]');
    const timecodeEl = q('[data-mako-timecode]');
    const progName = q('[data-mako-progname]');
    const playBtn = q('[data-mako-play]');
    const undoBtn = q('[data-mako-undo]');
    const resetBtn = q('[data-mako-reset]');
    const picker = q('[data-mako-picker]');
    const matteEl = q('.mk-matte');
    const videos = [...root.querySelectorAll('.mk-video[data-media]')];
    const bars = [...root.querySelectorAll('[data-bar]')];
    if (!stage || !tracksEl || !body) return;

    let state = start();
    let history = [];
    let sel = new Set();
    let fresh = new Set();
    let changed = new Set(); // clips changed in place, which flash once
    let layout = new Map(); // where each clip sat at the last render
    let grid = false;
    let ph = 4.8; // playhead, seconds
    let playing = false;
    let visible = false;
    let lastFrame = 0;
    let matteColor = '#ff8a3d';
    let statusTimer = 0;
    let fast = false;

    const seq = () => state.sequences[state.current];
    const byId = (id) => seq().clips.find((c) => c.id === id);
    const button = (tool) => q(`.mk-tool[data-mako-tool="${tool}"]`);

    /* ---------- Status and bars ---------- */
    let said = null; // the last message, kept while a feature is set up unseen
    const say = (text, warn) => {
      said = { text, warn };
      if (fast) return;
      statusEl.textContent = text;
      statusEl.classList.toggle('is-warn', !!warn);
      statusEl.classList.add('is-fresh');
      window.clearTimeout(statusTimer);
      statusTimer = window.setTimeout(() => statusEl.classList.remove('is-fresh'), 2200);
    };

    const showBar = (name) => {
      if (!bars.some((bar) => bar.dataset.bar === name)) return;
      bars.forEach((bar) => bar.classList.toggle('is-current', bar.dataset.bar === name));
    };

    /* ---------- Monitor ---------- */
    // The layers under the playhead, top track first; a nest contributes its
    // own clips.
    const frameAt = (t) => {
      const layers = [];
      // a nest (even a nest in a nest) shows its own clips
      // a frozen nest shows its clips at the moment it holds
      const collect = (c, at = t) => {
        if (c.type !== 'nest') return layers.push(c);
        const rel = c.freezeAt != null ? c.freezeAt : at - c.start;
        c.pieces
          .filter((p) => p.kind === 'video' && p.start <= rel && rel < p.end)
          .sort((a, b) => b.offset - a.offset)
          .forEach((p) => {
            const piece = { ...p, start: c.start + p.start, end: c.start + p.end };
            if (c.freezeAt != null && p.freezeAt == null) piece.freezeAt = (rel - p.start) * (p.speed || 1) + (p.from || 0);
            collect(piece, c.start + rel);
          });
      };
      seq()
        .clips.filter((c) => c.kind === 'video' && c.start <= t && t < c.end)
        .sort((a, b) => b.track - a.track)
        .forEach((c) => collect(c));

      const frame = { bg: null, fg: null, adjust: false };
      for (const c of layers) {
        if (c.type === 'adjust') {
          frame.adjust = true;
          continue;
        }
        if (c.type === 'matte') {
          frame.bg = { matte: c.color };
          break;
        }
        const layer = { media: c.media, scale: c.scale || 1, clip: c };
        if (layer.scale < 1 && !frame.fg) {
          frame.fg = layer; // a smaller picture: what's under it still shows
          continue;
        }
        if (!frame.fg || frame.fg.media !== layer.media) frame.bg = layer;
        break;
      }
      return frame;
    };

    // iPhones load only a video's metadata until it plays, and a seek then
    // hides the poster and shows black. So a video with no frame yet keeps
    // its poster and plays a moment, muted, to load; then it's paused and
    // seeked like the rest.
    const prime = (video) => {
      if (video.dataset.primed) return;
      video.dataset.primed = 'true';
      const p = video.play();
      if (!p) return;
      p.then(() => {
        if (!playing) video.pause();
        renderMonitor();
      }).catch(() => delete video.dataset.primed);
    };

    // A still holds one frame: its video stays paused there.
    const sync = (video, local, frozen) => {
      const d = video.duration || 8;
      const at = ((local % d) + d) % d;
      if (playing && !frozen) {
        if (video.paused) video.play().catch(() => {});
        if (Math.abs(video.currentTime - at) > 0.3) video.currentTime = at;
      } else if (video.readyState < 2) {
        prime(video);
      } else {
        if (!video.paused) video.pause();
        if (Math.abs(video.currentTime - at) > 0.04) video.currentTime = at;
      }
    };

    const renderMonitor = () => {
      const f = frameAt(ph);
      videos.forEach((video) => {
        const media = video.dataset.media;
        const layer = f.fg && f.fg.media === media ? f.fg : f.bg && f.bg.media === media ? f.bg : null;
        video.classList.toggle('is-on', !!layer);
        video.classList.toggle('is-front', !!layer && layer === f.fg);
        video.style.setProperty('--s', layer ? layer.scale : 1);
        const [tx, ty] = (layer && layer.clip.pos) || [0, 0];
        video.style.setProperty('--tx', `${tx}%`);
        video.style.setProperty('--ty', `${ty}%`);
        const c = layer && layer.clip;
        video.style.filter = c && c.look ? LOOK : '';
        if (c && c.freezeAt != null) sync(video, c.freezeAt, true);
        else if (c) sync(video, (ph - c.start) * (c.speed || 1) + (c.from || 0));
        else if (!video.paused) video.pause();
      });
      const matte = f.bg && f.bg.matte;
      if (matte) matteEl.style.setProperty('--matte', matte);
      matteEl.classList.toggle('is-on', !!matte);
      screen.classList.toggle('is-adjust', f.adjust);
      screen.classList.toggle('is-grid', grid);
    };

    // QC Check's markers, along the ruler: a span for a gap, a tick for a clip.
    const markersEl = document.createElement('span');
    markersEl.className = 'mk-markers';
    ruler.appendChild(markersEl);
    const renderMarkers = () => {
      markersEl.innerHTML = (seq().markers || [])
        .map((m) => `<span class="mk-marker" style="--x:${pct(m.start)}%;--w:${pct(Math.max(m.end - m.start, 0.12))}%;--c:${m.color}" title="${esc(m.note)}"></span>`)
        .join('');
    };

    const renderPlayhead = () => {
      body.style.setProperty('--ph', (ph / SPAN).toFixed(4));
      timecodeEl.textContent = timecode(ph);
      renderMonitor();
    };

    /* ---------- Timeline ---------- */
    const clipHtml = (c, i) => {
      const cls = ['mk-clip', `mk-clip--${c.type}`];
      if (c.kind === 'audio') cls.push('mk-clip--audio');
      if (fresh.has(c.id)) cls.push('is-new');
      if (c.end - c.start <= TINY + 0.001) cls.push('is-tiny');
      const style = `--x:${pct(c.start)}%;--w:${pct(c.end - c.start)}%;--d:${i}${c.color ? `;--c:${c.color}` : ''}`;
      return (
        `<button type="button" class="${cls.join(' ')}" data-clip="${c.id}" style="${style}"` +
        ` aria-pressed="${sel.has(c.id)}" aria-label="${esc(c.label)}, ${c.kind === 'video' ? 'V' : 'A'}${c.track}"` +
        `${c.kind === 'audio' ? ' tabindex="-1"' : ''}>` +
        `${c.fx ? '<span class="mk-tag">fx</span>' : ''}<span class="mk-clip-name">${esc(c.label)}</span></button>`
      );
    };

    const rowHtml = (kind, track) => {
      const s = seq();
      const name = `${kind === 'video' ? 'V' : 'A'}${track}`;
      const clips = s.clips
        .filter((c) => c.kind === kind && c.track === track)
        .sort((a, b) => a.start - b.start)
        .map(clipHtml)
        .join('');
      const cls = track === 1 ? (kind === 'video' ? ' is-v1' : ' is-a1') : '';
      return `<div class="mk-row${cls}" data-kind="${kind}" data-track="${track}"><span class="mk-head">${name}</span><span class="mk-lane">${clips}</span></div>`;
    };

    const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
    const renderTracks = () => {
      const s = seq();
      const focused = document.activeElement && document.activeElement.dataset ? document.activeElement.dataset.clip : null;
      const animate = !fast && !still.matches && typeof Element.prototype.animate === 'function';
      const before = new Map();
      if (animate) tracksEl.querySelectorAll('[data-clip]').forEach((el) => before.set(el.dataset.clip, el.getBoundingClientRect()));
      const was = layout;
      layout = new Map(s.clips.map((c) => [String(c.id), `${state.current}|${c.kind}|${c.track}|${c.start.toFixed(3)}`]));
      const rows = [];
      for (let t = s.v; t >= 1; t -= 1) rows.push(rowHtml('video', t));
      for (let t = 1; t <= s.a; t += 1) rows.push(rowHtml('audio', t));
      tracksEl.innerHTML = rows.join('');
      if (animate) {
        // a clip that changed track or time slides there from where it was
        let n = 0;
        tracksEl.querySelectorAll('[data-clip]').forEach((el) => {
          const id = el.dataset.clip;
          const old = before.get(id);
          if (changed.has(Number(id))) el.animate([{ filter: 'brightness(1.9)' }, { filter: 'brightness(1)' }], { duration: 700, easing: 'ease-out' });
          if (!old || !was.has(id) || was.get(id) === layout.get(id)) return;
          const now = el.getBoundingClientRect();
          const dx = old.left - now.left;
          const dy = old.top - now.top;
          if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
          el.style.zIndex = '2'; // above the lanes it passes
          const move = el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], {
            duration: 620,
            delay: n * 110,
            easing: EASE,
            fill: 'backwards',
          });
          n += 1;
          const done = () => (el.style.zIndex = '');
          move.onfinish = done;
          move.oncancel = done;
        });
      }
      fresh = new Set();
      changed = new Set();
      if (focused) {
        const again = tracksEl.querySelector(`[data-clip="${focused}"]`);
        if (again) again.focus({ preventScroll: true });
      }
    };

    const renderTabs = (newIndex) => {
      tabsEl.innerHTML = state.sequences
        .map(
          (s, i) =>
            `<button type="button" class="mk-seqtab${i === newIndex ? ' is-new' : ''}" role="tab" data-tab="${i}" aria-selected="${i === state.current}" tabindex="${i === state.current ? 0 : -1}">${esc(s.name)}</button>`
        )
        .join('');
      progName.textContent = seq().name;
    };

    const renderControls = () => {
      undoBtn.disabled = !history.length;
      resetBtn.disabled = !history.length && !grid && state.sequences.length === 1;
      const gridBtn = button('grid');
      if (gridBtn) gridBtn.setAttribute('aria-pressed', String(grid));
    };

    const render = (newTab) => {
      renderTabs(newTab);
      renderTracks();
      renderMarkers();
      renderControls();
      renderPlayhead();
    };

    /* ---------- Editing ---------- */
    const commit = () => {
      history.push(clone(state));
      if (history.length > 40) history.shift();
    };

    const overlaps = (kind, track, from, to, ignore) =>
      seq().clips.some((c) => c.kind === kind && c.track === track && c !== ignore && c.start < to - 0.001 && c.end > from + 0.001);

    // First track from `track` up with room for [from, to), adding one if needed.
    const freeTrack = (kind, track, from, to) => {
      const s = seq();
      let t = Math.max(1, track);
      while (overlaps(kind, t, from, to)) t += 1;
      if (kind === 'video' && t > s.v) s.v = t;
      if (kind === 'audio' && t > s.a) s.a = t;
      return t;
    };

    const selectedWithPartners = () => {
      const ids = new Set(sel);
      sel.forEach((id) => {
        const c = byId(id);
        if (c && c.link) ids.add(c.link);
      });
      return [...ids].map(byId).filter(Boolean);
    };

    const select = (id, add) => {
      const c = byId(id);
      if (!c) return;
      const ids = [c.id, c.link].filter(Boolean);
      if (add) {
        const on = !sel.has(c.id);
        ids.forEach((i) => (on ? sel.add(i) : sel.delete(i)));
      } else {
        sel = new Set(ids);
      }
      renderTracks();
    };

    // Adjustment layers and mattes: over the selection, else from the playhead.
    const place = (type, extra) => {
      const chosen = selectedWithPartners().filter((c) => c.kind === 'video');
      let from;
      let to;
      let above;
      if (chosen.length) {
        from = Math.min(...chosen.map((c) => c.start));
        to = Math.max(...chosen.map((c) => c.end));
        above = Math.max(...chosen.map((c) => c.track));
      } else {
        from = Math.min(ph, SPAN - 1);
        to = Math.min(from + 5, SPAN);
        const under = seq().clips.filter((c) => c.kind === 'video' && c.start < to && c.end > from);
        above = under.length ? Math.max(...under.map((c) => c.track)) : 0;
      }
      if (above + 1 > MAX_TRACKS || (above + 1 === MAX_TRACKS && overlaps('video', MAX_TRACKS, from, to))) {
        say('That’s plenty of tracks for one demo. Try Undo or Reset.', true);
        return null;
      }
      commit();
      const track = freeTrack('video', above + 1, from, to);
      if (track > MAX_TRACKS) {
        state = history.pop();
        say('That’s plenty of tracks for one demo. Try Undo or Reset.', true);
        return null;
      }
      const clip = { id: state.nextId++, kind: 'video', track, start: from, end: to, type, ...extra };
      seq().clips.push(clip);
      fresh.add(clip.id);
      sel = new Set([clip.id]);
      render();
      return clip;
    };

    /* ---------- Color Picker ---------- */
    // Premiere's picker: saturation/brightness field, hue strip, HSB/RGB
    // values and a hex field. The color is kept as hue/saturation/brightness.
    const field = picker.querySelector('[data-pk-field]');
    const hue = picker.querySelector('[data-pk-hue]');
    const hexInput = picker.querySelector('[data-pk-hex]');
    const pk = { h: 24, s: 0.76, v: 1 };

    const toRgb = ({ h, s, v }) => {
      const f = (n) => {
        const k = (n + h / 60) % 6;
        return v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
      };
      return [f(5), f(3), f(1)].map((x) => Math.round(x * 255));
    };
    const toHex = (rgb) => `#${rgb.map((x) => x.toString(16).padStart(2, '0')).join('')}`;
    const fromHex = (hex) => {
      const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
      if (!m) return null;
      const n = parseInt(m[1], 16);
      const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((x) => x / 255);
      const max = Math.max(r, g, b);
      const d = max - Math.min(r, g, b);
      let h = 0;
      if (d) {
        if (max === r) h = ((g - b) / d) % 6;
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h *= 60;
        if (h < 0) h += 360;
      }
      return { h, s: max ? d / max : 0, v: max };
    };

    const renderPicker = () => {
      const rgb = toRgb(pk);
      const hex = toHex(rgb);
      picker.style.setProperty('--h', pk.h.toFixed(1));
      picker.style.setProperty('--s', pk.s.toFixed(4));
      picker.style.setProperty('--b', pk.v.toFixed(4));
      picker.querySelector('[data-pk-new]').style.background = hex;
      picker.querySelector('[data-pk-old]').style.background = matteColor;
      const values = { h: Math.round(pk.h) % 360, s: Math.round(pk.s * 100), v: Math.round(pk.v * 100), r: rgb[0], g: rgb[1], bl: rgb[2] };
      Object.entries(values).forEach(([k, n]) => {
        const el = picker.querySelector(`[data-pk="${k}"]`);
        if (el) el.textContent = n;
      });
      if (document.activeElement !== hexInput) hexInput.value = hex.slice(1).toUpperCase();
      field.setAttribute('aria-valuenow', String(values.s));
      field.setAttribute('aria-valuetext', `Saturation ${values.s}%, brightness ${values.v}%`);
      hue.setAttribute('aria-valuenow', String(values.h));
      return hex;
    };

    const openPicker = () => {
      Object.assign(pk, fromHex(matteColor));
      renderPicker();
      picker.classList.add('is-open');
    };

    const closePicker = () => picker.classList.remove('is-open');

    const finishMatte = () => {
      matteColor = toHex(toRgb(pk));
      closePicker();
      const clip = place('matte', { label: 'Color Matte', color: matteColor });
      if (clip) {
        ph = Math.min(Math.max(ph, clip.start), clip.end - 0.05);
        renderPlayhead();
        say(`Color matte on V${clip.track}, ${seconds(clip.end - clip.start)} long.`);
      }
    };

    const dragOn = (el, apply) => {
      el.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        el.setPointerCapture(event.pointerId);
        const go = (e) => {
          const r = el.getBoundingClientRect();
          apply(Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)));
          renderPicker();
        };
        go(event);
        const up = () => {
          el.removeEventListener('pointermove', go);
          el.removeEventListener('pointerup', up);
          el.removeEventListener('pointercancel', up);
        };
        el.addEventListener('pointermove', go);
        el.addEventListener('pointerup', up);
        el.addEventListener('pointercancel', up);
      });
    };
    dragOn(field, (x, y) => {
      pk.s = x;
      pk.v = 1 - y;
    });
    dragOn(hue, (x, y) => {
      pk.h = Math.min(359.9, (1 - y) * 360);
    });

    const clamp01 = (n) => Math.min(1, Math.max(0, n));
    field.addEventListener('keydown', (event) => {
      const step = event.shiftKey ? 0.1 : 0.02;
      const keys = { ArrowLeft: ['s', -step], ArrowRight: ['s', step], ArrowUp: ['v', step], ArrowDown: ['v', -step] };
      if (!keys[event.key]) return;
      event.preventDefault();
      const [k, d] = keys[event.key];
      pk[k] = clamp01(pk[k] + d);
      renderPicker();
    });
    hue.addEventListener('keydown', (event) => {
      const d = { ArrowUp: 3, ArrowRight: 3, ArrowDown: -3, ArrowLeft: -3 }[event.key];
      if (!d) return;
      event.preventDefault();
      pk.h = (pk.h + (event.shiftKey ? d * 5 : d) + 360) % 360;
      renderPicker();
    });
    hexInput.addEventListener('input', () => {
      const hsv = fromHex(hexInput.value);
      if (!hsv) return;
      Object.assign(pk, hsv);
      renderPicker();
    });
    picker.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !event.target.closest('button')) {
        event.preventDefault();
        finishMatte();
      }
    });

    const unnest = () => {
      const s = seq();
      const nests = selectedWithPartners().filter((c) => c.type === 'nest' && c.kind === 'video');
      if (!nests.length) {
        const any = s.clips.some((c) => c.type === 'nest');
        say(any ? 'Select the nested sequence first, then press Unnest.' : 'There’s no nested sequence in this timeline.', true);
        return;
      }
      commit();
      const made = [];
      nests.forEach((nest) => {
        const audio = s.clips.find((c) => c.id === nest.link);
        s.clips = s.clips.filter((c) => c !== nest && c !== audio);
        const pairs = {};
        nest.pieces.forEach((p) => {
          const base = p.kind === 'video' ? nest.track : audio ? audio.track : 1;
          const from = nest.start + p.start;
          const to = nest.start + p.end;
          const track = freeTrack(p.kind, base + p.offset, from, to);
          const clip = {
            id: state.nextId++, kind: p.kind, track, start: from, end: to, type: p.type, label: p.label,
            media: p.media, scale: p.scale, speed: p.speed, pos: p.pos, from: p.from, fx: p.fx,
            look: p.look, color: p.color, freezeAt: p.freezeAt, pieces: p.pieces,
          };
          if (p.pair) {
            if (pairs[p.pair]) {
              clip.link = pairs[p.pair].id;
              pairs[p.pair].link = clip.id;
            } else pairs[p.pair] = clip;
          }
          s.clips.push(clip);
          made.push(clip);
        });
      });
      sel = new Set(made.map((c) => c.id));
      fresh = new Set(sel);
      render();
      say(`${made.length} clips back in place, effects kept.`);
    };

    const deleteEmptyTracks = () => {
      const s = seq();
      const plan = {};
      let gone = 0;
      ['video', 'audio'].forEach((kind) => {
        const count = kind === 'video' ? s.v : s.a;
        const used = [...new Set(s.clips.filter((c) => c.kind === kind).map((c) => c.track))].sort((a, b) => a - b);
        const keep = used.length ? used : [1];
        plan[kind] = keep;
        gone += count - keep.length;
      });
      if (!gone) {
        say('No empty tracks to delete.');
        return;
      }
      commit();
      const apply = () => {
        ['video', 'audio'].forEach((kind) => {
          const map = new Map(plan[kind].map((t, i) => [t, i + 1]));
          s.clips.filter((c) => c.kind === kind).forEach((c) => (c.track = map.get(c.track)));
          if (kind === 'video') s.v = plan.video.length;
          else s.a = plan.audio.length;
        });
        render();
        say(`Deleted ${gone} empty track${gone === 1 ? '' : 's'}.`);
      };
      // the empty rows fold away first
      const going = [...tracksEl.querySelectorAll('.mk-row')].filter(
        (row) => !plan[row.dataset.kind].includes(Number(row.dataset.track))
      );
      if (still.matches || !going.length) return apply();
      going.forEach((row) => {
        row.style.height = `${row.offsetHeight}px`;
        void row.offsetHeight;
        row.style.height = '';
        row.classList.add('is-going');
      });
      window.setTimeout(apply, 320);
    };

    const newSequence = () => {
      if (state.sequences.length >= 4) {
        say('Four sequences is plenty here. Try Reset.', true);
        return;
      }
      commit();
      const name = `Sequence ${pad(state.sequences.length + 1)}`;
      const open = seq();
      state.sequences.push({ name, v: Math.max(3, open.v), a: Math.max(2, open.a), clips: [] });
      state.current = state.sequences.length - 1;
      sel = new Set();
      ph = 0;
      render(state.current);
      say(`${name}, with the same settings.`);
    };


    /* ---------- The buttons added in Mako 1.1 and 1.2 ---------- */
    const MEDIA = ['clip', 'nest', 'still'];
    const videoSel = () => selectedWithPartners().filter((c) => c.kind === 'video');
    const holdsMedia = (c) => c.kind === 'video' && MEDIA.includes(c.type);
    const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

    // Called after commit() when the demo runs out of tracks: undo the half-made edit.
    const tooMany = () => {
      state = history.pop();
      render();
      say('That’s plenty of tracks for one demo. Try Undo or Reset.', true);
      return null;
    };

    const finish = (made) => {
      sel = new Set(made.map((c) => c.id));
      fresh = new Set(sel);
      render();
    };

    // Shift-click: an adjustment layer of its own over each clip.
    const adjustEach = () => {
      let clips = videoSel().filter(holdsMedia);
      if (!clips.length) clips = seq().clips.filter(holdsMedia);
      if (!clips.length) return say('There are no clips to cover.', true);
      commit();
      const made = [];
      for (const c of clips.sort((a, b) => a.start - b.start)) {
        const track = freeTrack('video', c.track + 1, c.start, c.end);
        if (track > MAX_TRACKS) return tooMany();
        const clip = { id: state.nextId++, kind: 'video', track, start: c.start, end: c.end, type: 'adjust', label: 'Adjustment Layer' };
        seq().clips.push(clip);
        made.push(clip);
      }
      finish(made);
      say(`${plural(made.length, 'adjustment layer')}, one over each clip.`);
    };

    // Ctrl/⌘-click: a short layer centred on every cut, all on one track.
    const adjustCuts = () => {
      let clips = videoSel().filter(holdsMedia);
      if (!clips.length) clips = seq().clips.filter(holdsMedia);
      const cuts = [];
      clips.forEach((a) =>
        clips.forEach((b) => {
          if (a !== b && Math.abs(a.end - b.start) < 0.005 && !cuts.some((t) => Math.abs(t - a.end) < 0.005)) cuts.push(a.end);
        })
      );
      if (!cuts.length) return say('No cuts between these clips: a cut is where one clip ends and the next starts.', true);
      cuts.sort((a, b) => a - b);
      const spans = cuts.map((t, i) => [
        Math.max(t - CUT / 2, i ? (cuts[i - 1] + t) / 2 : 0),
        Math.min(t + CUT / 2, i < cuts.length - 1 ? (t + cuts[i + 1]) / 2 : SPAN),
      ]);
      commit();
      let track = Math.max(...clips.map((c) => c.track)) + 1;
      while (spans.some(([a, b]) => overlaps('video', track, a, b))) track += 1;
      if (track > MAX_TRACKS) return tooMany();
      if (track > seq().v) seq().v = track;
      const made = spans.map(([a, b]) => {
        const clip = { id: state.nextId++, kind: 'video', track, start: a, end: b, type: 'adjust', label: 'Adj' };
        seq().clips.push(clip);
        return clip;
      });
      finish(made);
      say(`${plural(made.length, 'adjustment layer')} on the cuts, ${seconds(CUT)} each, on V${track}.`);
    };

    const staircase = () => {
      const clips = videoSel().sort((a, b) => a.start - b.start || a.track - b.track);
      if (clips.length < 2) return say('Select two or more clips for Staircase.', true);
      commit();
      let prev = Math.min(...clips.map((c) => c.track)) - 1;
      for (const c of clips) {
        let t = prev + 1;
        while (overlaps('video', t, c.start, c.end, c)) t += 1;
        if (t > MAX_TRACKS) return tooMany();
        if (t > seq().v) seq().v = t;
        c.track = t;
        prev = t;
      }
      render();
      say(`Staircase: ${plural(clips.length, 'clip')}, each a track higher.`);
    };

    const unstaircase = () => {
      const clips = videoSel().sort((a, b) => a.start - b.start);
      if (!clips.length) return say('Select the clips to bring back down.', true);
      const low = Math.min(...clips.map((c) => c.track));
      commit();
      let moved = 0;
      for (const c of clips) {
        let t = low;
        while (t < c.track && overlaps('video', t, c.start, c.end, c)) t += 1;
        if (t !== c.track) moved += 1;
        c.track = t;
      }
      if (!moved) {
        history.pop();
        return say('These clips are as low as they fit already.');
      }
      render();
      say(`Unstaircase: ${plural(moved, 'clip')} back down to V${low}.`);
    };

    // The clip to freeze: the selected one under the playhead, else the first
    // selected one, else the top clip under the playhead that holds media.
    const freezeSource = () => {
      const chosen = videoSel().filter(holdsMedia);
      const under = (c) => c.start <= ph && ph < c.end;
      return (
        chosen.find(under) ||
        chosen.sort((a, b) => a.start - b.start)[0] ||
        seq()
          .clips.filter((c) => holdsMedia(c) && under(c))
          .sort((a, b) => b.track - a.track)[0]
      );
    };

    // In place, as Mako 1.2.2 does it: the clip goes to 0.01% speed and its
    // In point to the frame to hold, so it shows that frame for its whole
    // length. Shift-click cuts it at the playhead and 3 s later and freezes
    // that piece, so the clip plays on after it.
    const SLOW = ' [0.01%]';
    const freeze = (hold) => {
      const c = freezeSource();
      if (!c) return say('Put the playhead over a clip, or select one.', true);
      if (c.type === 'still') return say('That clip can’t be frozen: it’s a still.', true);
      const t = c.start <= ph && ph < c.end ? ph : c.start;
      // the moment it holds: source time for a clip, nest time for a nest
      const local = c.freezeAt != null ? c.freezeAt : (t - c.start) * (c.type === 'nest' ? 1 : c.speed || 1) + (c.from || 0);
      const name = (c.label || '').replace(/ \[[\d.]+%\]$/, '');
      commit();
      let piece = c;
      if (hold && c.type === 'clip') {
        const end = Math.min(c.end, t + HOLD);
        const at = (x) => (c.freezeAt != null ? 0 : (x - c.start) * (c.speed || 1)) + (c.from || 0);
        const split = (from, to) => {
          const part = { ...clone(c), id: state.nextId++, start: from, end: to, from: at(from) };
          delete part.link;
          seq().clips.push(part);
          return part;
        };
        if (end < c.end - 0.001) split(end, c.end);
        if (t > c.start + 0.001) {
          piece = split(t, end);
          c.end = t;
        } else c.end = end;
      }
      piece.freezeAt = local;
      piece.label = `${name}${SLOW}`;
      delete piece.speed;
      changed.add(piece.id);
      sel = new Set([piece.id]);
      render();
      say(hold ? `${name} frozen for ${seconds(piece.end - piece.start)}, then plays on.` : `${name} frozen in place. No new layer.`);
    };

    const fit = (fill) => {
      let clips = videoSel().filter((c) => c.type === 'clip' || c.type === 'still');
      if (!clips.length) {
        const top = seq()
          .clips.filter((c) => (c.type === 'clip' || c.type === 'still') && c.kind === 'video' && c.start <= ph && ph < c.end)
          .sort((a, b) => b.track - a.track)[0];
        if (top) clips = [top];
      }
      if (!clips.length) return say('Select a clip, or put the playhead over one.', true);
      const off = clips.filter((c) => (c.scale || 1) !== 1 || c.pos);
      if (!off.length) return say(fill ? 'Already fills the frame.' : 'Already fits the frame.');
      commit();
      off.forEach((c) => {
        c.scale = 1;
        delete c.pos;
      });
      render();
      say(`${plural(off.length, 'clip')} scaled to ${fill ? 'fill' : 'fit'} the frame, centred.`);
    };

    const clearEffects = () => {
      const clips = videoSel().filter((c) => c.type !== 'adjust' && c.type !== 'matte');
      if (!clips.length) return say('Select clips first, then press Clear Effects.', true);
      const withFx = clips.filter((c) => c.fx || c.look);
      if (!withFx.length) return say('No added effects on these clips.');
      commit();
      withFx.forEach((c) => {
        delete c.fx;
        delete c.look;
      });
      render();
      say('Effects cleared. Motion and Opacity stay.');
    };

    const qcCheck = (clear) => {
      const s = seq();
      if (clear) {
        if (!(s.markers || []).length) return say('No QC markers to clear.');
        commit();
        s.markers = [];
        render();
        return say('QC markers cleared.');
      }
      commit();
      s.markers = [];
      // close every gap where nothing at all is on any track
      let closed = 0;
      for (;;) {
        let cursor = 0;
        let gap = null;
        for (const c of [...s.clips].sort((a, b) => a.start - b.start)) {
          if (c.start > cursor + 0.01) {
            gap = [cursor, c.start];
            break;
          }
          cursor = Math.max(cursor, c.end);
        }
        if (!gap) break;
        const len = gap[1] - gap[0];
        s.clips.forEach((c) => {
          if (c.start >= gap[1] - 0.001) {
            c.start -= len;
            c.end -= len;
          }
        });
        closed += 1;
      }
      // mark what's left: black stretches, and clips of a frame or two
      const end = Math.max(0, ...s.clips.map((c) => c.end));
      const shows = (t) => s.clips.some((c) => c.kind === 'video' && c.type !== 'adjust' && c.start <= t && t < c.end);
      for (let t = 0, from = null; t <= end; t += 0.04) {
        const black = t < end && !shows(t);
        if (black && from === null) from = t;
        if (!black && from !== null) {
          s.markers.push({ start: from, end: t, color: QC.gap, note: 'Mako QC: gap' });
          from = null;
        }
      }
      s.clips
        .filter((c) => c.kind === 'video' && c.end - c.start <= TINY + 0.001)
        .forEach((c) => s.markers.push({ start: c.start, end: c.end, color: QC.tiny, note: 'Mako QC: tiny clip' }));
      s.markers.sort((a, b) => a.start - b.start);
      if (!closed && !s.markers.length) {
        history.pop();
        return say('QC Check: nothing to fix.');
      }
      if (s.markers.length) ph = Math.min(s.markers[0].start, SPAN - 0.04);
      render();
      const parts = [];
      if (closed) parts.push(`closed ${plural(closed, 'gap')}`);
      const tiny = s.markers.filter((m) => m.color === QC.tiny).length;
      const gaps = s.markers.length - tiny;
      if (gaps) parts.push(`marked ${plural(gaps, 'black gap')}`);
      if (tiny) parts.push(`marked ${plural(tiny, 'tiny clip')}`);
      const text = parts.join(', ');
      say(`${text[0].toUpperCase()}${text.slice(1)}.`);
    };

    const nest = () => {
      const s = seq();
      const chosen = selectedWithPartners();
      const videos = chosen.filter((c) => c.kind === 'video');
      if (!videos.length) return say('Select the clips to nest first.', true);
      const audios = chosen.filter((c) => c.kind === 'audio');
      const from = Math.min(...chosen.map((c) => c.start));
      const to = Math.max(...chosen.map((c) => c.end));
      const baseV = Math.min(...videos.map((c) => c.track));
      const baseA = audios.length ? Math.min(...audios.map((c) => c.track)) : 0;
      const blocked = s.clips.some(
        (c) =>
          !chosen.includes(c) &&
          ((c.kind === 'video' && c.track === baseV) || (c.kind === 'audio' && c.track === baseA)) &&
          c.start < to - 0.001 &&
          c.end > from + 0.001
      );
      if (blocked) return say('Select clips that sit next to each other to nest them.', true);
      commit();
      state.nests = (state.nests || 1) + 1;
      const name = `Nested Sequence ${pad(state.nests)}`;
      const pieces = chosen.map((c) => ({
        kind: c.kind, offset: c.track - (c.kind === 'video' ? baseV : baseA), start: c.start - from, end: c.end - from,
        type: c.type, label: c.label, media: c.media, scale: c.scale, speed: c.speed, pos: c.pos, from: c.from,
        fx: c.fx, look: c.look, color: c.color, freezeAt: c.freezeAt, pieces: c.pieces,
        pair: c.link && chosen.some((o) => o.id === c.link) ? String(Math.min(c.id, c.link)) : undefined,
      }));
      s.clips = s.clips.filter((c) => !chosen.includes(c));
      const v = { id: state.nextId++, kind: 'video', track: baseV, start: from, end: to, type: 'nest', label: name, pieces };
      s.clips.push(v);
      const made = [v];
      if (audios.length) {
        const a = { id: state.nextId++, kind: 'audio', track: baseA, start: from, end: to, type: 'nest', label: name, link: v.id };
        v.link = a.id;
        s.clips.push(a);
        made.push(a);
      }
      finish(made);
      say(`Nested in place: ${name}.`);
    };

    // The gear: Mako's Settings, further down this page.
    const openSettings = () => {
      const target = document.querySelector('[data-mako-settings-section]');
      if (target) target.scrollIntoView({ behavior: still.matches ? 'auto' : 'smooth', block: 'start' });
    };

    const flash = (tool) => {
      const b = button(tool);
      if (!b) return;
      b.classList.add('is-pressed');
      window.setTimeout(() => b.classList.remove('is-pressed'), 240);
    };

    // mode: 'shift' or 'ctrl' for a modifier click (Ctrl, or ⌘ on a Mac).
    const press = (tool, mode) => {
      flash(tool);
      showBar(tool);
      if (tool !== 'matte') closePicker();
      switch (tool) {
        case 'adjustment': {
          if (mode === 'shift') return adjustEach();
          if (mode === 'ctrl') return adjustCuts();
          const clip = place('adjust', { label: 'Adjustment Layer' });
          if (clip) say(`Adjustment layer on V${clip.track}, ${seconds(clip.end - clip.start)} long.`);
          break;
        }
        case 'staircase':
          staircase();
          break;
        case 'unstaircase':
          unstaircase();
          break;
        case 'freeze':
          freeze(mode === 'shift');
          break;
        case 'fit':
          fit(mode === 'shift');
          break;
        case 'clearfx':
          clearEffects();
          break;
        case 'qc':
          qcCheck(mode === 'shift');
          break;
        case 'nest':
          nest();
          break;
        case 'settings':
          openSettings();
          break;
        case 'matte':
          openPicker();
          say('Pick a color for the matte.');
          break;
        case 'sequence':
          newSequence();
          break;
        case 'grid':
          grid = !grid;
          renderControls();
          renderMonitor();
          say(grid ? 'Transparency grid on.' : 'Transparency grid off.');
          break;
        case 'unnest':
          unnest();
          break;
        case 'tracks':
          deleteEmptyTracks();
          break;
        default:
      }
    };

    const undo = () => {
      if (!history.length) return;
      closePicker();
      state = history.pop();
      sel = new Set();
      render();
      say('Undone.');
    };

    const reset = (quiet) => {
      closePicker();
      state = start();
      layout = new Map();
      history = [];
      if (quiet) {
        // a fresh timeline starts without the last feature's message
        said = null;
        statusEl.textContent = '';
        statusEl.classList.remove('is-warn', 'is-fresh');
      }
      sel = new Set();
      grid = false;
      ph = 4.8;
      render();
      if (!quiet) say('Back to the start.');
    };

    /* ---------- Playback ---------- */
    const tick = (now) => {
      if (!playing) return;
      const dt = Math.min(0.1, (now - lastFrame) / 1000);
      lastFrame = now;
      ph += dt;
      if (ph >= SPAN) ph = 0;
      renderPlayhead();
      window.requestAnimationFrame(tick);
    };

    const setPlaying = (on) => {
      if (on === playing) return;
      playing = on;
      playBtn.classList.toggle('is-playing', on);
      playBtn.setAttribute('aria-label', on ? 'Pause' : 'Play');
      if (on) {
        lastFrame = performance.now();
        window.requestAnimationFrame(tick);
      }
      renderMonitor();
    };

    /* ---------- The feature reel ---------- */
    // Watch-only: the menu under the window plays one button at a time, a
    // pointer pressing it in the panel, then moves on by itself. Each
    // feature starts from a fresh timeline; a menu item jumps straight to it.
    const cursor = q('[data-mako-cursor]');
    const menu = q('[data-mako-menu]');
    const chips = [...root.querySelectorAll('[data-feature]')];
    const captions = [...root.querySelectorAll('[data-caption]')];
    const STOP = {};
    let tourRun = 0;
    let budget = 0;

    // In fast mode every pause is skipped but still counted, so a feature
    // can be set up instantly and its running time measured up front.
    const wait = (run, ms) => {
      if (fast) {
        budget += ms;
        return run === tourRun ? Promise.resolve() : Promise.reject(STOP);
      }
      return new Promise((resolve, reject) => {
        window.setTimeout(() => (run === tourRun ? resolve() : reject(STOP)), ms);
      });
    };

    const aim = (el, fx = 0.5, fy = 0.5) => {
      if (!el || fast) return;
      const s = stage.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      cursor.style.setProperty('--cx', `${r.left - s.left + r.width * fx}px`);
      cursor.style.setProperty('--cy', `${r.top - s.top + r.height * fy}px`);
    };
    const moveTo = (run, el, fx, fy, ms = 680) => {
      aim(el, fx, fy);
      return wait(run, ms);
    };
    const tap = (run) => {
      if (!fast) {
        cursor.classList.remove('is-click');
        void cursor.offsetWidth;
        cursor.classList.add('is-click');
      }
      return wait(run, 200);
    };

    // The pointer drags across the picker while the color follows it.
    const drag = (run, el, to, fx, fy, ms = 900) => {
      if (fast) {
        Object.assign(pk, to);
        renderPicker();
        return wait(run, ms);
      }
      return new Promise((resolve, reject) => {
        const from = { ...pk };
        cursor.classList.add('is-drag');
        aim(el, fx, fy);
        const t0 = performance.now();
        const step = (now) => {
          if (run !== tourRun) return reject(STOP);
          const k = Math.min(1, (now - t0) / ms);
          const e = k < 0.5 ? 2 * k * k : 1 - (-2 * k + 2) ** 2 / 2;
          ['h', 's', 'v'].forEach((key) => (pk[key] = from[key] + (to[key] - from[key]) * e));
          renderPicker();
          if (k < 1) window.requestAnimationFrame(step);
          else {
            cursor.classList.remove('is-drag');
            resolve();
          }
        };
        window.requestAnimationFrame(step);
      });
    };

    const clipEl = (id) => tracksEl.querySelector(`[data-clip="${id}"]`);
    const laneEl = (kind, track) => tracksEl.querySelector(`.mk-row[data-kind="${kind}"][data-track="${track}"] .mk-lane`);
    const scrubTour = async (run, t) => {
      await moveTo(run, ruler, t / SPAN, 0.5);
      await tap(run);
      ph = t;
      renderPlayhead();
    };

    const pick = async (run, id, add) => {
      await moveTo(run, clipEl(id), 0.4);
      await tap(run);
      if (add) select(id, true);
      else select(id);
      await wait(run, 260);
    };
    const pressTour = async (run, tool, after = 1300) => {
      await moveTo(run, button(tool));
      await tap(run);
      press(tool);
      await wait(run, after);
    };
    const deselect = async (run) => {
      await moveTo(run, laneEl('video', seq().v), 0.9);
      await tap(run);
      sel = new Set();
      renderTracks();
    };

    const scenes = {
      adjustment: async (run) => {
        await pick(run, 1);
        await pressTour(run, 'adjustment', 2200);
      },
      clearfx: async (run) => {
        await scrubTour(run, 2.0);
        await pick(run, 1);
        await pressTour(run, 'clearfx', 2200);
      },
      fit: async (run) => {
        await scrubTour(run, 9.6);
        await pick(run, 5);
        await pressTour(run, 'fit', 2300);
      },
      freeze: async (run) => {
        await pressTour(run, 'freeze', 2300);
      },
      matte: async (run) => {
        await scrubTour(run, 5.4);
        await moveTo(run, button('matte'));
        await tap(run);
        press('matte');
        await wait(run, 420);
        await moveTo(run, hue, 0.5, 1 - pk.h / 360);
        await drag(run, hue, { h: 212, s: pk.s, v: pk.v }, 0.5, 1 - 212 / 360, 700);
        await moveTo(run, field, pk.s, 1 - pk.v, 420);
        await drag(run, field, { h: 212, s: 0.8, v: 0.94 }, 0.8, 0.06, 700);
        await wait(run, 250);
        await moveTo(run, picker.querySelector('[data-picker="ok"]'));
        await tap(run);
        finishMatte();
        await wait(run, 2300);
      },
      sequence: async (run) => {
        await pressTour(run, 'sequence', 2300);
      },
      // three clips, so the steps read at a glance
      staircase: async (run) => {
        await scrubTour(run, 9.6);
        await pick(run, 1);
        await pick(run, 3, true);
        await pick(run, 5, true);
        await pressTour(run, 'staircase', 2400);
      },
      // starts on the stairs: the stepped clips are selected, then drop back
      unstaircase: async (run) => {
        await pick(run, 5);
        await pick(run, 3, true);
        await pick(run, 1, true);
        await pressTour(run, 'unstaircase', 2400);
      },
      nest: async (run) => {
        await scrubTour(run, 2.0);
        await pick(run, 1);
        await pressTour(run, 'nest', 2300);
      },
      unnest: async (run) => {
        await scrubTour(run, 5.2);
        await pick(run, 3);
        await pressTour(run, 'unnest', 2400);
      },
      qc: async (run) => {
        await deselect(run);
        await pressTour(run, 'qc', 2800);
      },
      grid: async (run) => {
        await scrubTour(run, 5.0);
        await pressTour(run, 'grid', 2500);
      },
      tracks: async (run) => {
        await pressTour(run, 'tracks', 2700);
      },
    };
    // Freeze Frame works on the fitted sunset, Unstaircase on the stairs.
    const NEEDS = { freeze: 'fit', unstaircase: 'staircase' };

    const ORDER = chips.map((chip) => chip.dataset.feature).filter((tool) => scenes[tool] && button(tool));
    let index = 0;

    // Sets the timeline up for a feature without showing the steps.
    const instantly = async (run, tools) => {
      stage.classList.add('is-instant');
      fast = true;
      budget = 0;
      try {
        for (const tool of tools) await scenes[tool](run);
      } finally {
        fast = false;
        void stage.offsetWidth;
        stage.classList.remove('is-instant');
      }
      return budget;
    };

    const prepare = async (run, tool) => {
      reset(true);
      if (NEEDS[tool]) await instantly(run, [NEEDS[tool]]);
      sel = new Set();
      renderTracks();
    };

    const durations = {};
    const mark = (tool, ms) => {
      chips.forEach((chip) => {
        const on = chip.dataset.feature === tool;
        chip.classList.toggle('is-current', on);
        chip.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) chip.style.setProperty('--dur', `${ms}ms`);
      });
      captions.forEach((cap) => cap.classList.toggle('is-current', cap.dataset.caption === tool));
      // Keep the current item in view inside the menu's own scroll.
      const chip = chips.find((c) => c.dataset.feature === tool);
      if (chip && menu.scrollWidth > menu.clientWidth) {
        const left = chip.offsetLeft - (menu.clientWidth - chip.offsetWidth) / 2;
        menu.scrollTo({ left: Math.max(0, left), behavior: still.matches ? 'auto' : 'smooth' });
      }
    };

    const play = async (from) => {
      tourRun += 1;
      const run = tourRun;
      index = from;
      setPlaying(false);
      stage.classList.add('is-touring');
      cursor.style.setProperty('--cx', `${stage.offsetWidth * 0.6}px`);
      cursor.style.setProperty('--cy', `${stage.offsetHeight * 0.4}px`);
      try {
        for (;;) {
          const tool = ORDER[index];
          if (durations[tool] === undefined) {
            await prepare(run, tool);
            durations[tool] = await instantly(run, [tool]);
          }
          await prepare(run, tool);
          cursor.classList.remove('is-drag');
          cursor.classList.add('is-on');
          mark(tool, durations[tool] + 400);
          root.classList.remove('is-reel');
          void root.offsetWidth;
          root.classList.add('is-reel');
          await wait(run, 400);
          await scenes[tool](run);
          index = (index + 1) % ORDER.length;
        }
      } catch (error) {
        if (error !== STOP) throw error;
      }
    };

    // Off screen, or with reduced motion: the reel stops on a finished frame.
    const stop = () => {
      tourRun += 1;
      cursor.classList.remove('is-on', 'is-drag');
      root.classList.remove('is-reel');
      closePicker();
    };

    const showStill = async (i) => {
      stop();
      const run = tourRun;
      index = i;
      const tool = ORDER[i];
      await prepare(run, tool);
      await instantly(run, [tool]);
      if (picker.classList.contains('is-open')) finishMatte();
      else if (said) say(said.text, said.warn);
      mark(tool, 0);
    };

    menu.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-feature]');
      if (!chip) return;
      const i = ORDER.indexOf(chip.dataset.feature);
      if (i < 0) return;
      if (still.matches || !visible) showStill(i);
      else play(i);
    });

    // Load the clips once the workspace is near; play while it's on screen.
    if ('IntersectionObserver' in window && ORDER.length) {
      new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          videos.forEach((v) => (v.preload = 'auto'));
        },
        { rootMargin: '600px 0px' }
      ).observe(stage);

      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            visible = entry.isIntersecting;
            if (!visible) return stop();
            if (still.matches) showStill(index);
            else play(index);
          });
        },
        { threshold: 0.45 }
      ).observe(stage);
    }

    render();
    if (ORDER.length) mark(ORDER[0], 0);
  };

  const boot = () => document.querySelectorAll('[data-mako-demo]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // The theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', boot);
})();
